import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PublicLayout from '../../components/PublicLayout';
import CheckIcon from '../../components/CheckIcon';
import '../../styles/PricingPage.css';

export default function PricingPage() {
  const [annual, setAnnual] = useState(false);
  const [currency, setCurrency] = useState('USD');
  
  const handleToggle = () => {
    setAnnual(!annual);
    // Analytics tracking will be handled by GTM
  };

  const handleCurrencyChange = (newCurrency) => {
    setCurrency(newCurrency);
    // Analytics tracking will be handled by GTM
  };

  // Currency symbols
  const currencySymbols = {
    USD: 'USD',
    EUR: 'EUR',
    INR: 'INR'
  };

  // Pricing data for Free and Individual plans
  const pricingData = {
    monthly: {
      USD: { Free: 0, Individual: 20, Team: 150 },
      EUR: { Free: 0, Individual: 17, Team: 127 },
      INR: { Free: 0, Individual: 1720, Team: 12750 }
    },
    annual: {
      USD: { Free: 0, Individual: 192, Team: 1440 },
      EUR: { Free: 0, Individual: 163, Team: 1224 },
      INR: { Free: 0, Individual: 16510, Team: 122400 }
    }
  };

  const getPrice = (planName) => {
    const period = annual ? 'annual' : 'monthly';
    return pricingData[period][currency][planName];
  };

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="pricing-hero">
        <div className="container">
          <h1>Simple, Transparent Pricing</h1>
          <p>Choose the plan that fits your team's needs</p>
          
          {/* Billing Toggle */}
          <div className="billing-toggle">
            <span className={!annual ? 'active' : ''}>Monthly</span>
            <label className="switch">
              <input 
                type="checkbox" 
                checked={annual} 
                onChange={handleToggle}
              />
              <span className="slider round"></span>
            </label>
            <span className={annual ? 'active' : ''}>Annual <span className="save-tag">Save 20%</span></span>
          </div>

          {/* Currency Toggle */}
          <div className="currency-toggle">
            <span>Currency:</span>
            <div className="currency-buttons">
              {Object.keys(currencySymbols).map((curr) => (
                <button
                  key={curr}
                  className={`currency-btn ${currency === curr ? 'active' : ''}`}
                  onClick={() => handleCurrencyChange(curr)}
                >
                  {curr}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="pricing-plans">
        <div className="container">
          <div className="pricing-grid">
            {/* Free Plan */}
            <div className="pricing-card">
              <div className="pricing-header">
                <h2>Free</h2>
                <div className="price">
                  <span className="amount">{currencySymbols[currency]} {getPrice('Free')}</span>
                  <span className="period">{annual ? ' per user/annum' : ' per user/month'}</span>
                </div>
                <p style={{ fontSize: '14px', color: '#64748b' }}>For individual-level features with limited access</p>
              </div>
              
              <div className="pricing-features">
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px'}}>5 document creation per month across categories</span>
                </div>
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>10 new chats (5 queries per chat)</span>
                </div>
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>5 document uploads in the knowledge hub</span>
                </div>
              </div>
              
              <div className="pricing-cta">
                <Link to="/signup" className="btn btn-secondary btn-full">Get Started Free</Link>
              </div>
            </div>

            {/* Individual Plan */}
            <div className="pricing-card highlighted">
              <div className="pricing-popular-tag">Most Popular</div>
              <div className="pricing-header">
                <h2>Individual</h2>
                <div className="price">
                  <span className="amount">{currencySymbols[currency]} {getPrice('Individual')}</span>
                  <span className="period">{annual ? ' per user/annum' : ' per user/month'}</span>
                </div>
                <p style={{ fontSize: '14px', color: '#64748b' }}>For individual-level features with unlimited access</p>
              </div>
              
              <div className="pricing-features">
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>Unlimited document creation across categories</span>
                </div>
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>Unlimited chats with unlimited queries per chat</span>
                </div>
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>Unlimited document uploads in the knowledge hub</span>
                </div>
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>Integration with Slack</span>
                </div>
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>Pre-defined model</span>
                </div>
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>Custom account list addition</span>
                </div>
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>Basic chat support</span>
                </div>
              </div>
              
              <div className="pricing-cta">
                <Link to="/signup" className="btn btn-primary btn-full">Start Individual Plan</Link>
              </div>
            </div>

            {/* Team Plan */}
            <div className="pricing-card">
              <div className="pricing-header">
                <h2>Team</h2>
                <div className="price">
                  <span className="period">Contact Sales</span>
                </div>
                <p style={{ fontSize: '14px', color: '#64748b' }}>For team-level features with flat-rate pricing</p>
              </div>
              
              <div className="pricing-features">
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>Unlimited document creation across categories</span>
                </div>
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>Unlimited chats with unlimited queries per chat</span>
                </div>
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>Unlimited document uploads in the knowledge hub</span>
                </div>
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>Integration with Slack</span>
                </div>
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>Integration with HubSpot</span>
                </div>
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>Option to select AI models</span>
                </div>
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>Centralized Billing</span>
                </div>
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>Shared Documents</span>
                </div>
                <div className="pricing-feature-item">
                  <CheckIcon /> <span style={{ fontSize: '13px' }}>Premium chat and call support</span>
                </div>
              </div>
              
              <div className="pricing-cta">
                <Link to="/signup" className="btn btn-secondary btn-full">Start Team Plan</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          
          <div className="faq-grid">
            <div className="faq-item">
              <h3>Is the Free plan really free?</h3>
              <p>Yes! Our Free plan is completely free with no hidden costs. You get 5 document creations per month, 10 chats with 5 queries each, and 5 document uploads to get started with Velox AI.</p>
            </div>
            
            <div className="faq-item">
              <h3>Can I change plans later?</h3>
              <p>Yes, you can upgrade or downgrade your plan at any time. If you upgrade, the changes take effect immediately. If you downgrade, the changes take effect at the end of your billing cycle.</p>
            </div>
            
            <div className="faq-item">
              <h3>How does Team plan billing work?</h3>
              <p>Team plans use flat-rate pricing starting at $150/month for small teams. Pricing scales with team size and includes all team features. For larger teams or custom pricing, please contact our sales team.</p>
            </div>
            
            <div className="faq-item">
              <h3>What integrations are available?</h3>
              <p>Individual plans include Slack integration. Team plans include both Slack and HubSpot integrations for comprehensive workflow automation.</p>
            </div>
            
            <div className="faq-item">
              <h3>Is there a minimum contract length?</h3>
              <p>Monthly plans have no minimum contract. Annual plans are billed for 12 months and automatically renew at the end of the term.</p>
            </div>
            
            <div className="faq-item">
              <h3>What kind of support do you offer?</h3>
              <p>Free plan includes community support. Individual plans include basic chat support. Team plans include premium chat and call support for dedicated assistance.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section cta-section">
        <div className="cta-background">
          <div className="cta-gradient"></div>
          <div className="cta-particles"></div>
        </div>
        
        <div className="container">
          <h2>Ready to Bring Ambient Intelligence to Your Sales?</h2>
          <p>Start with our Free Plan today — no credit card required</p>
          
          <div className="cta-buttons">
            <Link to="/signup" className="btn btn-primary btn-large">Get Started Free</Link>
            <Link to="/contact" className="btn btn-secondary">Have Questions? Contact Us</Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
} 